import React from "react";
import { useBranches } from "@api";
import {
  SectionTitle,
  SectionTagline,
} from "../components";
import usePaymentSettingsForm from "../hooks/usePaymentSettingsForm";

// Account numbers are shown by their last four digits only.
const accountEnding = (accountNumber) => accountNumber.substring(accountNumber.length - 4);

// The merchant's cash account, its financing account when it has one, and one account per branch
// that is paid into its own bank account. With branch accounts on the list, the merchant's cash
// account is named after the default branch and explains what still lands in it.
export const buildAccounts = (paymentSettings, branches) => {
  const { bankAccountAccountNumber = "", financingBankAccountAccountNumber = "" } = paymentSettings;

  const branchAccounts = branches
    .filter((branch) => !branch.isDefault && branch.bankAccountAccountNumber)
    .map((branch) => ({
      id: `branch-${branch.id}`,
      label: `Cash Account – ${branch.name}`,
      accountNumber: branch.bankAccountAccountNumber,
      note: `Payments for ${branch.name} invoices`,
    }));

  const defaultBranch = branches.find((branch) => branch.isDefault);
  const cashAccount = branchAccounts.length > 0 && defaultBranch
    ? {
        id: "cash",
        label: `Cash Account – ${defaultBranch.name}`,
        accountNumber: bankAccountAccountNumber,
        note: `Payments for ${defaultBranch.name} invoices and payments for invocies not linked to a branch`,
      }
    : { id: "cash", label: "Cash Account", accountNumber: bankAccountAccountNumber, note: null };

  return [
    cashAccount,
    financingBankAccountAccountNumber && {
      id: "trade",
      label: "Financing Account",
      accountNumber: financingBankAccountAccountNumber,
      note: null,
    },
    ...branchAccounts,
  ].filter(Boolean);
};

const BankAccounts = () => {
  const { merchant, paymentSettings } = usePaymentSettingsForm();
  const { data: branches = [] } = useBranches();

  if (!merchant) return null;

  const accounts = buildAccounts(paymentSettings, branches);

  return (
    <div className="flex flex-col items-center py-4 px-5 overflow-auto">
      <div className="py-8 w-full max-w-4xl flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-1">
          <SectionTitle>Bank accounts</SectionTitle>
          <SectionTagline>
            Payments will be sent to the following accounts. To change, contact{" "}
            <a className="text-primary underline" href="mailto:support@ontik.io">
              support@ontik.io
            </a>
          </SectionTagline>
        </div>

        <div className="w-full flex flex-col gap-y-4">
          {accounts.map((account) => (
            <div key={account.id} className="rounded-3xl bg-white shadow-[4px_4px_16px_0px_rgba(0,0,0,0.16)] py-6 px-6 w-full">
              <span className="text-m font-medium text-zinc-900 block">{account.label}</span>
              <span className="text-sm text-zinc-500 block">Account ending {accountEnding(account.accountNumber)}</span>
              {account.note && <span className="text-sm text-zinc-500 block">{account.note}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BankAccounts;
